"use client";

import { FC, ReactNode, useState, useRef, useLayoutEffect } from "react";
import cn from "classnames";
import { getDataAutomationId, getDataLinkAttributes } from "@/helpers";
import { Button } from "../Button";

export type ExpandableTextProps = {
  children: ReactNode;
  dld?: string;
  dlt?: string;
  linesToShow: number;
  expandTo?: "top" | "bottom";
  viewMore?: string;
  viewLess?: string;
};

export const ExpandableText: FC<ExpandableTextProps> = ({
  viewLess = "View less",
  viewMore = "View more",
  dld,
  dlt,
  children,
  linesToShow = 3,
  expandTo = "top",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [maxHeight, setMaxHeight] = useState(999);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const elm: HTMLDivElement | null = contentRef.current;
    if (!elm) return;
    const isTextClamped = (elm: HTMLDivElement) =>
      elm.scrollHeight > elm.clientHeight;

    const observer = new ResizeObserver((e) => {
      const target = e[0].target as HTMLDivElement;
      if (!isExpanded) {
        const clamped = isTextClamped(target);
        setIsTruncated(clamped);
      }

      setMaxHeight(target.scrollHeight);
    });

    observer.observe(elm);
    return () => observer.disconnect();
  }, [isExpanded]);

  const button = (
    <Button
      onClick={() => setIsExpanded(!isExpanded)}
      {...getDataLinkAttributes({ dld, dlt })}
      {...getDataAutomationId("expandable-text-button")}
      className={cn(
        expandTo === "top" ? "mt-4" : "mb-4",
        !isTruncated && !isExpanded && "hidden",
        "relative",
      )}
      variant="tertiary"
      size="m"
      icon={isExpanded ? "arrowUp" : "arrowDown"}
      iconPosition="right"
    >
      {isExpanded ? viewLess : viewMore}
    </Button>
  );
  const text = (
    <div
      ref={contentRef}
      className={cn(
        !isExpanded && isTruncated
          ? "mask-[linear-gradient(to_bottom,black_calc(100%-24px),transparent_100%)]"
          : "mask-none",
        "text-body-s transition-[max-height] duration-500 ease-in-out",
      )}
      style={{
        maxHeight: !isExpanded ? `${linesToShow * 1.5}em` : `${maxHeight}px`,
      }}
    >
      {children}
    </div>
  );

  return (
    <div className="overflow-hidden">
      {expandTo === "top" ? (
        <>
          {text} {button}
        </>
      ) : (
        <>
          {button} {text}
        </>
      )}
    </div>
  );
};
